import { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useAuth from "../../hooks/store/auth";
import { Layout as AuthLayout } from "../../common/Layout/auth/layout";

const Page = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyAction } = useAuth();
  const emailFromQuery = searchParams.get("email") || "";
  const codeFromQuery = searchParams.get("code") || "";
  const hasAutoSubmittedRef = useRef(false);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: emailFromQuery,
      code: codeFromQuery,
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Email is required"),
      code: Yup.string().max(6).required("code is required"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        const result = await verifyAction({ email: values.email, code: values.code });
        if (result?.type === "auth/verify/fulfilled") {
          navigate(
            `/update-password?email=${encodeURIComponent(values.email)}&code=${encodeURIComponent(values.code)}`
          );
          return;
        }
        helpers.setStatus({ success: false });
        helpers.setErrors({
          submit: result?.payload || "Verification failed",
        });
        helpers.setSubmitting(false);
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    hasAutoSubmittedRef.current = false;
  }, [emailFromQuery, codeFromQuery]);

  useEffect(() => {
    if (!emailFromQuery || !codeFromQuery || hasAutoSubmittedRef.current) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    formik.submitForm();
  }, [codeFromQuery, emailFromQuery, formik]);

  return (
    <AuthLayout>
      <Box
        sx={{
          flex: "1 1 auto",
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            px: 3,
            py: "100px",
            width: "100%",
          }}
        >
          <div>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Typography variant="h4">Verify Email</Typography>
            </Stack>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  error={!!(formik.touched.email && formik.errors.email)}
                  fullWidth
                  helperText={formik.touched.email && formik.errors.email}
                  label="Email Address"
                  name="email"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="email"
                  value={formik.values.email}
                />
                <TextField
                  error={!!(formik.touched.code && formik.errors.code)}
                  fullWidth
                  helperText={formik.touched.code && formik.errors.code}
                  label="Code"
                  name="code"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="text"
                  value={formik.values.code}
                />
              </Stack>
              {formik.errors.submit && (
                <Typography color="error" sx={{ mt: 3 }} variant="body2">
                  {formik.errors.submit}
                </Typography>
              )}
              <Button
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                type="submit"
                variant="contained"
              >
                Verify Email
              </Button>
              <Typography
                color="text.secondary"
                variant="body2"
                textAlign={"center"}
                sx={{ p: 1 }}
              >
                Don&apos;t have an account? &nbsp;
                <Button color="secondary" to="/register" component={Link}>
                  SignUp
                </Button>
              </Typography>
            </form>
          </div>
        </Box>
      </Box>
    </AuthLayout>
  );
};

Page.getLayout = (page) => <AuthLayout>{page}</AuthLayout>;

export default Page;
